import {
  OPERATION_STEP_GROUP_KEY,
  PRODUCTION_STEP_GROUP_KEY,
  PRODUCTION_TAB_LOCKED_REASON,
} from 'constants/uiConstants';
import type { IUnit } from 'interfaces/IInput';
import {
  IInputStep,
  IInputStepApplication,
  IInputStepProduction,
  InputStepType,
} from 'interfaces/IInputStep';
import { IInputTask, InputTaskType } from 'interfaces/IInputTask';

// Operation costs on a Production task (OP-2399) are either $/ha, like on any
// other task, or priced per unit of yield ($/t), such as baling. A cost is
// yield-linked when its unit is the production step's yield unit: its rate is
// then the task's Expected yield, kept in step with it, so rate × cost × area
// is the spend and every total counts it as Operation. The unit is the only
// marker, as there's no field for it.

export type ProductionCostBasis = 'perHa' | 'perYield';

export const PRODUCTION_COST_STEP_TYPES: InputStepType[] = [
  InputStepType.Application,
];

export const getProductionStep = (
  task: IInputTask
): IInputStepProduction | null =>
  (task.steps ?? []).find(
    (step): step is IInputStepProduction =>
      step.stepType === InputStepType.Production
  ) ?? null;

/** An Operation cost on a Production task, $/ha or yield-linked. */
export const isProductionCostStep = (
  step: IInputStep,
  task: IInputTask
): step is IInputStepApplication =>
  task.taskType === InputTaskType.Production &&
  step.stepType === InputStepType.Application;

const isSameUnit = (a: IUnit | null, b: IUnit | null): boolean => {
  if (!a || !b) return false;
  if (a.globalId && b.globalId) return a.globalId === b.globalId;
  return a.unit.trim().toLowerCase() === b.unit.trim().toLowerCase();
};

/** An Operation cost priced per unit of the task's yield, e.g. $/t. */
export const isYieldLinkedStep = (
  step: IInputStep,
  task: IInputTask
): step is IInputStepApplication =>
  isProductionCostStep(step, task) &&
  isSameUnit(step.appUnit, getProductionStep(task)?.yieldUnit ?? null);

/**
 * The patch that sets a Production task's Operation cost to $/ha (rate 1, no
 * unit) or per unit of yield (the yield's unit; the rate follows through
 * syncYieldLinkedSteps).
 */
export const getProductionCostBasisPatch = (
  basis: ProductionCostBasis,
  task: IInputTask
): Pick<IInputStepApplication, 'rate' | 'appUnit'> => {
  const productionStep = getProductionStep(task);
  return basis === 'perYield'
    ? {
        rate: productionStep?.yield ?? null,
        appUnit: productionStep?.yieldUnit ?? null,
      }
    : { rate: 1, appUnit: null };
};

/** Sets every yield-linked step's rate to the task's Expected yield. */
export const syncYieldLinkedSteps = (task: IInputTask): IInputTask => {
  if (task.taskType !== InputTaskType.Production) return task;
  const expectedYield = getProductionStep(task)?.yield ?? null;
  let changed = false;
  const steps = (task.steps ?? []).map((step) => {
    if (!isYieldLinkedStep(step, task) || step.rate === expectedYield) {
      return step;
    }
    changed = true;
    return { ...step, rate: expectedYield };
  });
  return changed ? { ...task, steps } : task;
};

/**
 * Whether a step type can be added to the task now. A Production task holds
 * one production step and any number of Operation costs, in either order,
 * like other tasks' steps.
 */
export const canAddStepTypeToTask = (
  stepType: InputStepType,
  task: IInputTask,
  isAllowed: (stepType: InputStepType, taskType: InputTaskType) => boolean
): boolean => {
  if (!isAllowed(stepType, task.taskType)) return false;
  if (task.taskType !== InputTaskType.Production) return true;
  if (stepType !== InputStepType.Production) return true;
  // One production step, on a task holding only Operation costs so far.
  return (task.steps ?? []).every((step) => isProductionCostStep(step, task));
};

/** A Production task's picker tabs: its product and its costs. */
export const getAddCategoryKeysForTask = (
  task: IInputTask
): string[] | undefined =>
  task.taskType === InputTaskType.Production
    ? [PRODUCTION_STEP_GROUP_KEY, OPERATION_STEP_GROUP_KEY]
    : undefined;

/**
 * Tabs shown but disabled when adding, with why: Production once the task
 * has its product, since a Production task holds one.
 */
export const getAddLockedCategoryReasonsForTask = (
  task: IInputTask
): Record<string, string> | undefined =>
  task.taskType === InputTaskType.Production &&
  !canAddStepTypeToTask(
    InputStepType.Production,
    task,
    // Always allowed on a Production task; only the one-product rule applies.
    () => true
  )
    ? { [PRODUCTION_STEP_GROUP_KEY]: PRODUCTION_TAB_LOCKED_REASON }
    : undefined;

/**
 * Yield-linked cost per ha for a yield scenario: the scenario's yield
 * replaces the Expected yield the stored rate holds.
 */
export const getYieldLinkedCostPerHa = (
  step: IInputStepApplication,
  scenarioYield: number
): number => (step.cost ?? 0) * scenarioYield;
